import React from "react";
import { Button, Col, Input, Label, Row, TabPane } from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { editDetailsProfile } from "../../../../slices/thunks";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { capitalizeFirstLetter, states } from "../../../../utils/utils";
import Cleave from "cleave.js/react";
import NextButton from "../../components/NextButton";
import BackButton from "../../components/BackButton";

const ProfileStepBroker = ({
  toggleVerticalTab,
  activeVerticalTab,
  currentUser,
  setCurrentUser,
  profileStepBroker,
}) => {
  // console.log(currentUser);
  const [loadingUpdate, setLoadingUpdate] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const dispatch = useDispatch();

  const initialValues = {
    companyAddress: currentUser?.companyAddress || "",
    state: currentUser?.state || "",
    zipCode: currentUser?.zipCode || "",
    firstName: currentUser?.firstName || "",
    lastName: currentUser?.lastName || "",
    phoneNumber: currentUser?.phoneNumber || "",
    email: currentUser?.email || "",
    city: currentUser?.city || "",
  };

  const validationSchema = Yup.object().shape({
    companyAddress: Yup.string().required("Company Address is required"),
    state: Yup.string().required("State is required"),
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    zipCode: Yup.string()
      .matches(/^[0-9]{1,5}$/, "Zip Code is not valid")
      .required("Zip Code is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone Number must be 10 digits long")
      .required("Phone Number is required"),
    email: Yup.string().email().required("Email is required"),
    city: Yup.string().required("City is required"),
  });

  const handleSubmit = async (values) => {
    try {
      setLoadingUpdate(true);
      Swal.fire({
        title: "Please wait...",
        html: "Your information is being updated.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      const response = await dispatch(editDetailsProfile(values));

      console.log(response);

      if (!response.error) {
        toggleVerticalTab(activeVerticalTab + 1);
        setCurrentUser({
          ...currentUser,
          ...values,
        });

        Swal.close();
      } else {
        setErrorMessage(response.error);
        Swal.fire({
          title: "Error",
          text: response.error,
          icon: "error",
        });
      }

      setLoadingUpdate(false);
    } catch (error) {
      setLoadingUpdate(true);
      Swal.fire(
        "Error",
        "There was a problem updating your information. Please try again.",
        "error"
      );
      setLoadingUpdate(false);
      console.error(error);
    }
  };

  return (
    <TabPane tabId={profileStepBroker}>
      <div>
        <h5>Broker Details</h5>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, values }) => {
          const isComplete = Object.values(values).every((val) => val !== "");
          const allowNext = isComplete && !loadingUpdate;

          return (
            <Form className="border rounded-4  p-3 mb-2">
              <Row className="mb-2">
                <Col sm={6}>
                  <Label htmlFor="firstName">First Name</Label>
                  <Field
                    name="firstName"
                    type="text"
                    className="form-control"
                  />
                  <ErrorMessage
                    name="firstName"
                    component="div"
                    className="text-danger"
                  />
                </Col>

                <Col sm={6}>
                  <Label htmlFor="lastName" className="form-label">
                    Last name
                  </Label>
                  <Field
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastName"
                  />
                  <ErrorMessage
                    name="lastName"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={6} className="mb-2">
                  <Label htmlFor="email" className="form-label">
                    Email
                  </Label>
                  <Field
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    disabled
                  />
                </Col>

                <Col xs={6} className="mb-2">
                  <Label htmlFor="phoneNumber" className="form-label">
                    Phone Number
                  </Label>
                  <Field name="phoneNumber">
                    {({ field, form, meta }) => (
                      <Cleave
                        {...field}
                        className="form-control"
                        options={{
                          numericOnly: true,
                          delimiters: [, "(", ")-", "-"],
                          blocks: [0, 0, 3, 3, 4],
                          delimiterLazyShow: true,
                        }}
                        onChange={(e) => {
                          form.setFieldValue("phoneNumber", e.target.rawValue);
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="phoneNumber"
                    component="div"
                    className="text-danger"
                  />
                </Col>
                <Col sm={6}>
                  <Label htmlFor="companyAddress" className="form-label">
                    Company Address
                  </Label>
                  <Field
                    type="text"
                    className="form-control"
                    id="companyAddress"
                    name="companyAddress"
                  />
                  <ErrorMessage
                    name="companyAddress"
                    component="div"
                    className="text-danger"
                  />
                </Col>
                <Col sm={6}>
                  <Label htmlFor="state" className="form-label">
                    State
                  </Label>
                  <Field as="select" name="state" className="form-control">
                    <option value="" disabled>
                      Select State
                    </option>
                    {states.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="state"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>

              <Row className="mb-2">
                <Col sm={6}>
                  <Label htmlFor="city" className="form-label">
                    City/Town
                  </Label>
                  <Field
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                  />
                  <ErrorMessage
                    name="city"
                    component="div"
                    className="text-danger"
                  />
                </Col>
                <Col sm={6}>
                  <Label htmlFor="zipCode" className="form-label">
                    Zip Code
                  </Label>
                  <Field
                    onInput={(e) => {
                      if (e.target.value.length > 5)
                        e.target.value = e.target.value.slice(0, 5);
                    }}
                    pattern="\d*"
                    type="number"
                    className="form-control"
                    id="zipCode"
                    name="zipCode"
                  />
                  <ErrorMessage
                    name="zipCode"
                    component="div"
                    className="text-danger"
                  />
                </Col>
              </Row>

              <div className="d-flex align-items-start gap-3 mt-4">
                <BackButton
                  type={"button"}
                  back={() => toggleVerticalTab(activeVerticalTab - 1)}
                />
                <NextButton type={"submit"} disabled={loadingUpdate} />
              </div>
            </Form>
          );
        }}
      </Formik>
    </TabPane>
  );
};

export default ProfileStepBroker;
